import type { ElementDto, TextElementDto } from "@repo/api/overlays";
import type { TextLayoutStyle } from "@repo/video-engine-i2v/utils/text-layout";

export type Point = { x: number; y: number };
export type Size = { w: number; h: number };
export type Box = Point & Size;
export type Resolution = { width: number; height: number };

export type MeasureTextElement = (element: TextElementDto) => Size | null;

export function getElementBox(
  element: ElementDto,
  measureTextElement: MeasureTextElement,
): Box | null {
  const size =
    element.type === "TEXT" ? measureTextElement(element) : element.size;

  if (!size) {
    return null;
  }

  return {
    x: element.position.x,
    y: element.position.y,
    w: size.w,
    h: size.h,
  };
}

export function isPointInBox(point: Point, box: Box) {
  return (
    point.x >= box.x &&
    point.x <= box.x + box.w &&
    point.y >= box.y &&
    point.y <= box.y + box.h
  );
}

export function boxesIntersect(a: Box, b: Box) {
  return (
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
  );
}

export function getBoxFromPoints(a: Point, b: Point): Box {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(b.x - a.x),
    h: Math.abs(b.y - a.y),
  };
}

export function translateBox(box: Box, dx: number, dy: number): Box {
  return { ...box, x: box.x + dx, y: box.y + dy };
}

export function isSameBox(a: Box, b: Box) {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

export function getBoundingBox(boxes: Box[]): Box | null {
  if (boxes.length === 0) {
    return null;
  }

  const left = Math.min(...boxes.map((box) => box.x));
  const top = Math.min(...boxes.map((box) => box.y));
  const right = Math.max(...boxes.map((box) => box.x + box.w));
  const bottom = Math.max(...boxes.map((box) => box.y + box.h));

  return { x: left, y: top, w: right - left, h: bottom - top };
}

export function findTopmostAt<T extends { box: Box }>(
  items: T[],
  point: Point,
): T | null {
  return items.find((item) => isPointInBox(point, item.box)) ?? null;
}

export function clientToComposition(
  client: Point,
  canvasRect: Pick<DOMRect, "left" | "top" | "width" | "height">,
  resolution: Resolution,
): Point {
  return {
    x: ((client.x - canvasRect.left) * resolution.width) / canvasRect.width,
    y: ((client.y - canvasRect.top) * resolution.height) / canvasRect.height,
  };
}

export function getBoxStyle(box: Box, resolution: Resolution) {
  return {
    left: `${(box.x / resolution.width) * 100}%`,
    top: `${(box.y / resolution.height) * 100}%`,
    width: `${(box.w / resolution.width) * 100}%`,
    height: `${(box.h / resolution.height) * 100}%`,
  };
}

const measurements = new Map<string, Size>();

export function measureText(text: string, style: TextLayoutStyle): Size {
  // key is stable because getTextLayoutStyle always builds the style in the same property order.
  const key = JSON.stringify([text, style]);
  const cached = measurements.get(key);

  if (cached) {
    return cached;
  }

  const node = document.createElement("div");
  Object.assign(node.style, style, {
    position: "absolute",
    top: "-10000px",
    left: "0",
    display: "inline-block",
    visibility: "hidden",
  });
  node.textContent = text;

  document.body.appendChild(node);
  const { width, height } = node.getBoundingClientRect();
  document.body.removeChild(node);

  const size = { w: width, h: height };
  measurements.set(key, size);

  return size;
}
